import { useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
} from "recharts";
import {
  DashFrame, KpiCard, FilterSelect, DataTable, ChartCaption, ChartTitle, LegendDots, tooltipPct,
  usePalette, GRID, AXIS, TOOLTIP_STYLE, CURSOR,
} from "./kit";
import { euro } from "../lib/format";

const initial = { year: "All years", category: "All categories", reason: "All reasons" };

export default function ReturnsDash({ rows }) {
  const [f, setF] = useState(initial);
  const c = usePalette();
  const years = [...new Set(rows.map((r) => r.month.slice(0, 4)))];
  const categories = [...new Set(rows.map((r) => r.category))];
  const reasons = [...new Set(rows.map((r) => r.reason))];

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (f.year === "All years" || r.month.startsWith(f.year)) &&
          (f.category === "All categories" || r.category === f.category) &&
          (f.reason === "All reasons" || r.reason === f.reason)
      ),
    [rows, f]
  );

  const kpis = useMemo(() => {
    const pairs = new Map();
    filtered.forEach((r) => {
      const k = `${r.month}|${r.category}`;
      const prev = pairs.get(k) || { delivered: r.delivered, returned: 0 };
      prev.returned += r.returned;
      pairs.set(k, prev);
    });
    let delivered = 0, returned = 0;
    pairs.forEach((p) => { delivered += p.delivered; returned += p.returned; });
    const byReason = {};
    filtered.forEach((r) => { byReason[r.reason] = (byReason[r.reason] || 0) + r.returned; });
    const topReason = Object.entries(byReason).sort((a, b) => b[1] - a[1])[0];
    const rate = delivered ? (returned / delivered) * 100 : 0;
    return { delivered, returned, rate, topReason, byReason };
  }, [filtered]);

  const trend = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.month] = m[r.month] || { delivered: 0, returned: 0 };
      m[r.month].returned += r.returned;
    });
    // delivered is per (month, category): recount from unique pairs
    const pairs = new Map();
    filtered.forEach((r) => {
      const k = `${r.month}|${r.category}`;
      if (!pairs.has(k)) { pairs.set(k, true); m[r.month].delivered += r.delivered; }
    });
    return Object.entries(m)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({
        month: month.slice(2), // YY-MM
        rate: v.delivered ? Number((((v.returned / v.delivered) * 100)).toFixed(2)) : 0,
      }));
  }, [filtered]);

  const byReason = useMemo(
    () =>
      Object.entries(kpis.byReason)
        .map(([name, returned]) => ({ name: name.replace(" ordered", "").replace(" on arrival", ""), returned }))
        .sort((a, b) => b.returned - a.returned),
    [kpis.byReason]
  );

  const byCategory = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.category] = m[r.category] || { delivered: 0, returned: 0 };
      m[r.category].returned += r.returned;
    });
    const seen = new Set();
    filtered.forEach((r) => {
      if (!seen.has(r.category)) { seen.add(r.category); m[r.category].delivered = r.delivered; }
      else { m[r.category].delivered += r.delivered; }
    });
    return Object.entries(m).map(([name, v]) => ({
      name: name.replace(" & Living", ""),
      rate: v.delivered ? Number(((v.returned / v.delivered) * 100).toFixed(2)) : 0,
    }));
  }, [filtered]);

  const maxCategoryRate = Math.max(...byCategory.map((r) => r.rate), 0);

  return (
    <DashFrame
      title="Customer returns analysis"
      testid="dash-returns"
      filters={
        <>
          <FilterSelect label="Period" testid="filter-period" value={f.year}
            options={["All years", ...years]} onChange={(v) => setF({ ...f, year: v })} />
          <FilterSelect label="Product category" testid="filter-category" value={f.category}
            options={["All categories", ...categories]} onChange={(v) => setF({ ...f, category: v })} />
          <FilterSelect label="Return reason" testid="filter-reason" value={f.reason}
            options={["All reasons", ...reasons]} onChange={(v) => setF({ ...f, reason: v })} />
        </>
      }
      onReset={() => setF(initial)}
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Units delivered" value={euro(kpis.delivered).replace("€", "")} sub="Sample units" accent={c.blue} />
        <KpiCard label="Units returned" value={euro(kpis.returned).replace("€", "")} accent={c.navy} />
        <KpiCard label="Return rate" value={`${kpis.rate.toFixed(2)}%`} sub="Returned ÷ delivered" accent={c.blue} />
        <KpiCard label="Top reason (problem area)" value={kpis.topReason ? kpis.topReason[0].replace(" ordered", "") : "—"}
          sub={kpis.topReason ? `${((kpis.topReason[1] / Math.max(kpis.returned, 1)) * 100).toFixed(0)}% of returns` : ""} accent={c.red} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <ChartTitle color={c.navy}>Return rate trend by month</ChartTitle>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="month" tick={{ ...AXIS, fontSize: 10 }} />
                <YAxis tick={AXIS} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={tooltipPct} contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="rate" name="Return rate" stroke={c.blue} strokeWidth="2.5" dot={false} activeDot={{ r: 4, fill: c.navy }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <LegendDots items={[{ label: "Return rate (lower is better)", color: c.blue, shape: "line" }]} />
        </div>
        <div>
          <ChartTitle color={c.navy}>Returned units by reason</ChartTitle>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byReason} layout="vertical" margin={{ top: 4, right: 16, left: 30, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={{ ...AXIS, fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={110} tick={AXIS} />
                <Tooltip formatter={(v) => `${v} units`} cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="returned" name="Returned units" radius={[0, 3, 3, 0]}>
                  {byReason.map((r, i) => <Cell key={r.name} fill={i === 0 ? c.red : c.navy} />)}
                  <LabelList dataKey="returned" position="right" style={{ fontSize: 10, fill: "#6D6B78" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <LegendDots items={[{ label: "Returned units", color: c.navy }, { label: "Largest reason — problem area", color: c.red }]} />
        </div>
      </div>

      <div className="mt-8">
        <ChartTitle color={c.navy}>Return rate by category (%)</ChartTitle>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byCategory} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={AXIS} />
              <YAxis tick={AXIS} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={tooltipPct} cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="rate" name="Return rate" radius={[3, 3, 0, 0]}>
                {byCategory.map((r) => <Cell key={r.name} fill={r.rate === maxCategoryRate ? c.red : c.blue} />)}
                <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 10, fill: "#6D6B78" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <LegendDots items={[{ label: "Return rate by category", color: c.blue }, { label: "Highest rate — problem area", color: c.red }]} />
        <ChartCaption>Units delivered and returned, filtered by period, category and reason.</ChartCaption>
      </div>

      <div className="mt-6">
        <DataTable
          testid="dash-returns-table"
          columns={[
            { key: "month", label: "Month" },
            { key: "category", label: "Category" },
            { key: "reason", label: "Reason" },
            { key: "returned", label: "Returned units", right: true },
            { key: "delivered", label: "Delivered units", right: true },
            { key: "share", label: "Share of month-category returns", right: true },
          ]}
          rows={filtered.slice(0, 60).map((r) => ({
            ...r,
            share: `${((r.returned / Math.max(r.delivered, 1)) * 100).toFixed(1)}%`,
          }))}
        />
      </div>
    </DashFrame>
  );
}
